/**
 * CorrectionsPanel — the closed-session correction ladder. A refund/adjustment against a CLOSED
 * session is posted AGAINST THE OPEN DRAWER and retains the closed original's frozen-Z snapshot — it
 * NEVER mutates a frozen Z. This panel reads the real list (GET /finance/caisse/:id/corrections,
 * cap finance.cloture.read) and posts new corrections (POST /finance/corrections,
 * cap finance.paiement.refund). Both caps are gated via useCan.
 *
 * The Table wires Loading/Error/Empty (3 of the 5 states) for free; the page owns offline/forbidden.
 */
import { useCallback, useState, type FormEvent } from "react";
import {
  Badge,
  Button,
  Card,
  FormField,
  ForbiddenState,
  Input,
  Modal,
  Select,
  Textarea,
  Table,
  useToast,
  type Column,
} from "@/ui";
import { useCan } from "@/auth";
import { useT } from "@/app";
import { ApiError } from "@/api";
import { token } from "@/design/theme";
import { FINANCE_CAPS, useCorrections, usePostCorrection } from "../api";
import type { CorrectionCaisse, CorrectionType, Sens } from "../types";
import { MoneyInput } from "./MoneyInput";
import { Money } from "./Money";

export interface CorrectionsPanelProps {
  labId: string | null;
  /** The OPEN drawer the corrections post against (and whose list we read). */
  sessionId: string;
  enabled: boolean;
}

export function CorrectionsPanel({ labId, sessionId, enabled }: CorrectionsPanelProps) {
  const { t } = useT();
  const toast = useToast();
  const canReadList = useCan(FINANCE_CAPS.clotureRead);
  const canPost = useCan(FINANCE_CAPS.paiementRefund);

  const listQuery = useCorrections(labId, sessionId, enabled && canReadList);
  const postMut = usePostCorrection(labId, sessionId);

  const [open, setOpen] = useState(false);
  const [type, setType] = useState<CorrectionType>("refund");
  const [sens, setSens] = useState<Sens>("sortie");
  const [montant, setMontant] = useState<number | null>(null);
  const [motif, setMotif] = useState("");
  const [origineId, setOrigineId] = useState("");
  const [factureId, setFactureId] = useState("");
  const [touched, setTouched] = useState(false);

  const montantError = touched && (montant == null || montant <= 0) ? t("finance.field.amountInvalid") : null;
  const motifError = touched && motif.trim().length < 3 ? t("finance.field.motifShort") : null;
  const valid = montant != null && montant > 0 && motif.trim().length >= 3;

  const reset = () => {
    setType("refund");
    setSens("sortie");
    setMontant(null);
    setMotif("");
    setOrigineId("");
    setFactureId("");
    setTouched(false);
  };

  // Stable close handler (keeps the Modal focus-trap from stealing focus mid-typing on re-render).
  const close = useCallback(() => {
    setType("refund");
    setSens("sortie");
    setMontant(null);
    setMotif("");
    setOrigineId("");
    setFactureId("");
    setTouched(false);
    setOpen(false);
  }, []);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!valid || !canPost) return;
    postMut.mutate(
      {
        type,
        sens,
        montant: montant ?? 0,
        motif: motif.trim(),
        ...(origineId.trim() ? { session_origine_id: origineId.trim() } : {}),
        ...(factureId.trim() ? { facture_id: factureId.trim() } : {}),
      },
      {
        onSuccess: () => {
          toast.success(t("finance.corr.success"));
          reset();
          setOpen(false);
        },
        onError: (err) => {
          toast.error(err instanceof ApiError ? err.message : t("finance.corr.title"));
        },
      },
    );
  };

  const columns: ReadonlyArray<Column<CorrectionCaisse>> = [
    {
      key: "type",
      header: t("finance.corr.col.type"),
      cell: (row) => (
        <Badge tone={row.type === "refund" ? "warn" : "neutral"}>
          {row.type === "refund" ? t("finance.corr.type.refund") : t("finance.corr.type.ajustement")}
        </Badge>
      ),
    },
    {
      key: "sens",
      header: t("finance.corr.col.sens"),
      cell: (row) => (
        <Badge tone={row.sens === "entree" ? "success" : "danger"}>
          {row.sens === "entree" ? t("finance.mvt.sens.entree") : t("finance.mvt.sens.sortie")}
        </Badge>
      ),
    },
    {
      key: "montant",
      header: t("finance.corr.col.montant"),
      align: "end",
      mono: true,
      cell: (row) => <Money centimes={row.sens === "sortie" ? -row.montant : row.montant} signed />,
    },
    {
      key: "origine",
      header: t("finance.corr.col.origine"),
      mono: true,
      cell: (row) => (
        <span className="nv-mono nv-ltr-island">{row.session_origine_id ? String(row.session_origine_id) : "—"}</span>
      ),
    },
    {
      key: "motif",
      header: t("finance.corr.col.motif"),
      cell: (row) => <span>{row.motif}</span>,
    },
  ];

  if (!canReadList) {
    return <ForbiddenState requiredCapabilities={[FINANCE_CAPS.clotureRead]} />;
  }

  const rows = listQuery.data ?? [];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-5)" }}>
      <Card
        title={t("finance.corr.title")}
        actions={
          <Button size="sm" disabled={!canPost} onClick={() => setOpen(true)}>
            + {t("finance.corr.add")}
          </Button>
        }
      >
        <p style={{ margin: "0 0 var(--nv-s-3)", color: token.slate, fontSize: "var(--nv-fs-label)" }}>
          {t("finance.corr.subtitle")}
        </p>
        <Table<CorrectionCaisse>
          columns={columns}
          rows={rows}
          rowKey={(r) => String(r.id)}
          caption={t("finance.corr.title")}
          loading={listQuery.isLoading}
          error={
            listQuery.isError
              ? { message: listQuery.error.message, onRetry: () => void listQuery.refetch() }
              : null
          }
          emptyTitle={t("finance.corr.empty.title")}
          emptyDescription={t("finance.corr.empty.desc")}
        />
      </Card>

      <Modal
        open={open}
        onClose={close}
        title={t("finance.corr.add")}
        footer={
          <>
            <Button variant="ghost" onClick={close} disabled={postMut.isPending}>
              {t("action.close")}
            </Button>
            <Button type="submit" form="finance-corr" loading={postMut.isPending} disabled={!canPost}>
              {t("finance.corr.submit")}
            </Button>
          </>
        }
      >
        <form id="finance-corr" onSubmit={submit} noValidate>
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-4)" }}>
            <FormField label={t("finance.corr.type")}>
              {(p) => (
                <Select
                  {...p}
                  value={type}
                  onChange={(e) => setType(e.target.value as CorrectionType)}
                  options={[
                    { value: "refund", label: t("finance.corr.type.refund") },
                    { value: "ajustement", label: t("finance.corr.type.ajustement") },
                  ]}
                />
              )}
            </FormField>

            <FormField label={t("finance.corr.sens")}>
              {(p) => (
                <Select
                  {...p}
                  value={sens}
                  onChange={(e) => setSens(e.target.value as Sens)}
                  options={[
                    { value: "sortie", label: t("finance.mvt.sens.sortie") },
                    { value: "entree", label: t("finance.mvt.sens.entree") },
                  ]}
                />
              )}
            </FormField>

            <FormField
              label={t("finance.corr.montant")}
              required
              {...(montantError ? { error: montantError } : {})}
            >
              {(p) => (
                <MoneyInput
                  {...p}
                  aria-label={t("finance.amount.aria")}
                  valueCentimes={montant}
                  onChangeCentimes={setMontant}
                  invalid={!!montantError}
                />
              )}
            </FormField>

            <FormField
              label={t("finance.corr.origine")}
              hint={t("finance.corr.origine.hint")}
            >
              {(p) => (
                <Input
                  {...p}
                  mono
                  className="nv-ltr-island"
                  autoComplete="off"
                  value={origineId}
                  onChange={(e) => setOrigineId(e.target.value)}
                />
              )}
            </FormField>

            <FormField
              label={t("finance.corr.factureId")}
              hint={t("finance.corr.factureId.hint")}
            >
              {(p) => (
                <Input
                  {...p}
                  mono
                  className="nv-ltr-island"
                  autoComplete="off"
                  value={factureId}
                  onChange={(e) => setFactureId(e.target.value)}
                />
              )}
            </FormField>

            <FormField
              label={t("finance.corr.motif")}
              hint={t("finance.corr.motif.hint")}
              required
              {...(motifError ? { error: motifError } : {})}
            >
              {(p) => (
                <Textarea
                  {...p}
                  rows={2}
                  value={motif}
                  invalid={!!motifError}
                  onChange={(e) => setMotif(e.target.value)}
                />
              )}
            </FormField>
          </div>
        </form>
      </Modal>
    </div>
  );
}
